import { useCallback, useMemo } from 'react';

interface NavigableItem {
    id: number;
}

export function useDrawerNavigation<T extends NavigableItem>(
    items: T[],
    activeItem: T | null | undefined,
    openItem: (item: T) => void,
) {
    const currentIndex = useMemo(() => {
        if (!activeItem) return -1;
        return items.findIndex((item) => item.id === activeItem.id);
    }, [activeItem, items]);

    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < items.length - 1;

    const goToPrevious = useCallback(() => {
        if (hasPrevious) {
            openItem(items[currentIndex - 1]);
        }
    }, [currentIndex, items, hasPrevious, openItem]);

    const goToNext = useCallback(() => {
        if (hasNext) {
            openItem(items[currentIndex + 1]);
        }
    }, [currentIndex, items, hasNext, openItem]);

    return {
        currentIndex,
        hasPrevious,
        hasNext,
        goToPrevious,
        goToNext,
    };
}
